import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { Search, Settings, X, Smartphone, Laptop, Watch, Headphones, Tablet, ArrowLeft, ShoppingCart } from "lucide-react";
import TeclyPullRefresh from "@/components/TeclyPullRefresh";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { BottomNavigation } from "@/components/BottomNavigation";
import { PageTransition } from "@/components/PageTransition";
import { ProductCard } from "@/components/ProductCard";
import { ProductDetail } from "@/components/ProductDetail";
import { ProductAdminPanel } from "@/components/ProductAdminPanel";
import { MenuDrawer } from "@/components/MenuDrawer";
import { useProducts, useProductCategories, type Product } from "@/hooks/useProducts";
import { useAdminRole } from "@/hooks/useAdminRole";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { CartDrawer } from "@/components/CartDrawer";
import { Badge } from "@/components/ui/badge";

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  smartphone: <Smartphone className="w-5 h-5" />,
  laptop: <Laptop className="w-5 h-5" />,
  watch: <Watch className="w-5 h-5" />,
  headphones: <Headphones className="w-5 h-5" />,
  tablet: <Tablet className="w-5 h-5" />,
};

const Prodotti = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const { totalItems } = useCart();

  const { products, loading, refetch } = useProducts();
  const { categories, refetch: refetchCategories } = useProductCategories();
  const { isAdmin } = useAdminRole(userId);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setUserId(data.user?.id || null);
    });
  }, []);

  // Pull-to-refresh
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const touchStartY = useRef(0);
  const isPulling = useRef(false);
  const PULL_THRESHOLD = 80;

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await Promise.all([refetch(), refetchCategories()]);
    setIsRefreshing(false);
  }, [refetch, refetchCategories]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const scrollTop = scrollRef.current?.scrollTop ?? 0;
    if (scrollTop <= 0) {
      touchStartY.current = e.touches[0].clientY;
      isPulling.current = true;
    }
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isPulling.current || isRefreshing) return;
    const diff = e.touches[0].clientY - touchStartY.current;
    if (diff > 0) {
      setPullDistance(Math.min(diff * 0.5, 120));
    }
  }, [isRefreshing]);

  const handleTouchEnd = useCallback(() => {
    if (pullDistance >= PULL_THRESHOLD && !isRefreshing) {
      handleRefresh();
    }
    setPullDistance(0);
    isPulling.current = false;
  }, [pullDistance, isRefreshing, handleRefresh]);

  const filteredProducts = useMemo(() => {
    let filtered = products.filter(p => p.is_active);

    if (selectedCategory) {
      filtered = filtered.filter(p => p.category_id === selectedCategory);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(product => {
        const name = product.name.toLowerCase();
        const desc = product.description?.toLowerCase() || "";
        const catName = product.category?.name?.toLowerCase() || "";
        return name.includes(query) || desc.includes(query) || catName.includes(query);
      });
    }

    return filtered;
  }, [products, selectedCategory, searchQuery]);

  // History-based back gesture for product detail
  useEffect(() => {
    if (selectedProduct) {
      window.history.pushState({ productDetail: true }, '');
      const handlePopState = () => {
        setSelectedProduct(null);
      };
      window.addEventListener('popstate', handlePopState);
      return () => window.removeEventListener('popstate', handlePopState);
    }
  }, [selectedProduct]);

  if (selectedProduct) {
    return (
      <ProductDetail 
        product={selectedProduct} 
        onBack={() => {
          window.history.back();
        }} 
      />
    );
  }

  return (
    <PageTransition>
      <div
        ref={scrollRef}
        className="min-h-screen bg-background pb-24 overflow-y-auto"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Pull to refresh indicator */}
        <TeclyPullRefresh pullDistance={pullDistance} isRefreshing={isRefreshing} />

        {/* Header */}
        <header className="sticky top-0 z-20 bg-background/80 backdrop-blur-lg border-b border-border/30">
          <div className="max-w-6xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                {!userId && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate("/")}
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </Button>
                )}
                <h1 className="text-2xl font-bold text-foreground">Prodotti</h1>
              </div>
              <div className="flex items-center gap-2">
                {/* Cart button */}
                <Button variant="ghost" size="icon" className="relative" onClick={() => setCartOpen(true)}>
                  <ShoppingCart className="w-5 h-5" />
                  {totalItems > 0 && (
                    <Badge className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-[10px]">
                      {totalItems}
                    </Badge>
                  )}
                </Button>
                {isAdmin && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowAdminPanel(!showAdminPanel)}
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    {showAdminPanel ? "Catalogo" : "Gestione"}
                  </Button>
                )}
              </div>
            </div>

            {!showAdminPanel && (
              <>
                {/* Search */}
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="Cerca prodotti..."
                    className="pl-10 h-12 bg-muted/50"
                  />
                  {searchQuery && (
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="absolute right-2 top-1/2 -translate-y-1/2"
                      onClick={() => setSearchQuery("")}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>

                {/* Category Tabs with icons */}
                <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                  <button
                    onClick={() => setSelectedCategory(null)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all flex-shrink-0 ${
                      selectedCategory === null
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : "bg-muted/50 text-muted-foreground hover:bg-muted"
                    }`}
                  >
                    Tutti
                  </button>
                  {categories.map(category => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all flex-shrink-0 ${
                        selectedCategory === category.id
                          ? "bg-primary text-primary-foreground shadow-sm"
                          : "bg-muted/50 text-muted-foreground hover:bg-muted"
                      }`}
                    >
                      {category.icon_url ? (
                        <img src={category.icon_url} alt="" className="w-5 h-5 rounded object-cover" />
                      ) : (
                        CATEGORY_ICONS[category.icon] || <Smartphone className="w-5 h-5" />
                      )}
                      {category.name}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </header>

        <main className="max-w-6xl mx-auto px-6 py-6">
          {showAdminPanel ? (
            <ProductAdminPanel 
              products={products}
              categories={categories}
              onRefresh={refetch}
              onRefreshCategories={refetchCategories}
            />
          ) : (
            <>
              {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {[...Array(8)].map((_, i) => (
                    <div key={i} className="aspect-square bg-muted animate-pulse rounded-xl" />
                  ))}
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="text-center py-16">
                  <p className="text-muted-foreground text-lg">Nessun prodotto trovato</p>
                  {searchQuery && (
                    <Button 
                      variant="link" 
                      onClick={() => setSearchQuery("")}
                      className="mt-2"
                    >
                      Cancella ricerca
                    </Button>
                  )}
                </div>
              ) : (
                <motion.div 
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ staggerChildren: 0.05 }}
                >
                  {filteredProducts.map((product, index) => (
                    <motion.div
                      key={product.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <ProductCard 
                        product={product}
                        onClick={() => setSelectedProduct(product)}
                      />
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </>
          )}
        </main>

        {/* Drawers */}
        <CartDrawer open={cartOpen} onOpenChange={setCartOpen} />
        {userId && (
          <>
            <MenuDrawer open={menuOpen} onOpenChange={setMenuOpen} />
            <BottomNavigation onMenuClick={() => setMenuOpen(true)} activeTab="prodotti" />
          </>
        )}
      </div>
    </PageTransition>
  );
};

export default Prodotti;
